import { UntypedFormGroup } from '@angular/forms';
import { Component, OnInit, Inject } from '@angular/core';
import { RtTripShopForm } from '@bds/railtrac-models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogOptionsBase } from '@bds/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RtShopFormService, TripShopFormRequest } from '../rt-shop-form.service';
import { ErrorHandlerService } from '@bds/railtrac-internal-services';
import { RtCarMechService, RtShopFactService } from '@bds/data-access';
import { RtCommodityShippedService } from '@bds/railtrac';
import { BdsCommodityService, BdsCustomerService } from '@bds/reference-data-access';
import { catchError, of, switchMap } from 'rxjs';
import { EquipmentService } from '@bds/equipment';

export class RtTripShopFormDialogOptions extends DialogOptionsBase<
    RtTripShopForm | RtTripShopForm[]
> {
    title: string;
    shopForm?: RtTripShopForm;
    mode?: 'create' | 'edit' | 'view';
    readonly?: boolean;
    carInit?: string;
    carNo?: string;
    shipDateTime?: Date;
    tripOrmId?: number;
    custNo?: string;
    generate = true;
}

@Component({
    templateUrl: './rt-shop-form-dialog.component.html',
    styleUrls: ['./rt-shop-form-dialog.component.scss'],
})
export class RtTripShopFormDialogComponent implements OnInit {
    form: UntypedFormGroup;
    shopForm: RtTripShopForm;
    loading = false;
    mode: 'create' | 'edit' | 'view';
    readonly = false;
    isInitialized = false;

    private editableFields = [
        'cleanSafeEntry',
        'shopComments',
        'specialRequirements',
        'additionalComments',
    ];

    constructor(
        public dialogRef: MatDialogRef<RtTripShopFormDialogComponent, RtTripShopForm>,
        @Inject(MAT_DIALOG_DATA) public data: RtTripShopFormDialogOptions,
        public snackbar: MatSnackBar,
        public errorService: ErrorHandlerService,
        public shopFormService: RtShopFormService,
        public shopFactService: RtShopFactService,
        public commodityShippedService: RtCommodityShippedService,
        public commodityService: BdsCommodityService,
        public equipmentService: EquipmentService,
        public carMechService: RtCarMechService,
        public customerService: BdsCustomerService,
    ) {}

    ngOnInit() {
        this.mode = this.data?.mode || 'create';
        this.readonly = this.data?.readonly || this.mode === 'view';

        if (this.readonly) {
            this.editableFields = ['shopComments', 'additionalComments'];
        }

        // Always call generate service method first
        this.generateShopForm();
    }

    private generateShopForm(): void {
        this.loading = true;

        // Prepare request data
        const request: TripShopFormRequest = {
            carInit: this.data?.shopForm?.carInit || this.data?.carInit || '',
            carNo: this.data?.shopForm?.carNo || this.data?.carNo || '',
            shipDateTime:
                this.data?.shopForm?.shipDateTime || this.data?.shipDateTime || new Date(),
            custNo: this.data?.shopForm?.custNo || this.data?.custNo || '',
        };

        // Validate required fields
        if (!request.carInit || !request.carNo || !request.shipDateTime) {
            this.loading = false;
            this.snackbar.open('Car Init, Car No, and Ship Date are required', 'Close', {
                duration: 5000,
            });
            this.dialogRef.close();
            return;
        }

        const observable = !this.data?.generate
            ? this.shopFormService.getShopForm(
                  this.data?.shopForm?.rtTripOrmId || this.data?.tripOrmId,
              )
            : this.shopFormService.generateShopForm(request);

        observable
            .pipe(
                catchError(() => {
                    this.loading = false;
                    this.snackbar.open('Failed to get shop form', 'Close', { duration: 5000 });
                    this.dialogRef.close();
                    return of(null);
                }),
                switchMap((result) => {
                    if (!result) return of(null);
                    this.shopForm = result;
                    this.initializeForm();
                    this.isInitialized = true;
                    this.form.patchValue(this.shopForm);
                    return this.commodityShippedService.readCombined([result?.rtTripOrmId]).pipe(
                        catchError((error) => {
                            console.error('Error reading combined commodity shipped:', error);
                            return of(null);
                        }),
                    );
                }),
                switchMap((commodityData) => {
                    const commCode = commodityData?.commoditiesShipped?.commodityCode;
                    return this.commodityService.read(commCode).pipe(
                        catchError((error) => {
                            console.error('Error reading commodity:', error);
                            return of(null);
                        }),
                    );
                }),
                switchMap((commodity) => {
                    this.form.controls['productName'].setValue(
                        commodity?.commodityShortName || commodity?.commodityName,
                    );
                    this.form.controls['stcc'].setValue(commodity?.stccCode);
                    return this.carMechService.readViaOdata(request.carInit, request.carNo).pipe(
                        catchError((error) => {
                            console.error('Error reading CarMech:', error);
                            return of(null);
                        }),
                    );
                }),
                switchMap((mech) => {
                    this.form.controls['lessorRiderNo'].setValue(mech?.lessorRiderNo);
                    this.form.controls['lessorBeginDate'].setValue(mech?.lessorBegDate);
                    this.form.controls['lessorEndDate'].setValue(mech?.lessorEndDate);
                    return this.equipmentService
                        .getEquipmentInfo(request.carInit, request.carNo)
                        .pipe(
                            catchError((error) => {
                                console.error('Error getting equipment info:', error);
                                return of([]);
                            }),
                        );
                }),
                switchMap((equipmentInfo) => {
                    this.form.controls['carCapacity'].setValue(
                        equipmentInfo[0]?.capacity?.toString() +
                            ' ' +
                            (equipmentInfo[0]?.capacity ? equipmentInfo[0].capacityUm : ''),
                    );
                    return this.shopFactService.read(request.custNo).pipe(
                        catchError(() => {
                            return of(null);
                        }),
                    );
                }),
                switchMap((shopData) => {
                    this.form.controls['shopName'].setValue(shopData?.shopShortName);
                    this.form.controls['shopContact'].setValue(shopData?.shopContact?.toString());
                    return this.customerService.read(request.custNo).pipe(
                        catchError(() => {
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe({
                next: (customerData) => {
                    if (!customerData) return;
                    this.loading = false;

                    this.form.controls['shopLocation'].setValue(customerData?.customerCity);
                    this.form.controls['email'].setValue(customerData?.customerEmail?.toString());
                    this.form.controls['faxNumber'].setValue(customerData?.customerFax);
                },
                error: (error) => {
                    this.loading = false;
                    console.error('Error generating shop form:', error);
                    this.snackbar.open('Failed to generate shop form', 'Close', { duration: 5000 });
                    // this.dialogRef.close();
                },
            });
    }

    private initializeForm(): void {
        // Create form using the service method with generated data
        this.form = this.shopFormService.createShopFormGroup(this.shopForm);

        // Set readonly state for all controls except editable ones
        this.setFormReadonlyState();
    }

    private setFormReadonlyState(): void {
        if (!this.form) return;

        // If in view mode, disable everything
        if (this.readonly) {
            this.form.disable();
        }

        // Disable all controls except the editable ones
        Object.keys(this.form.controls).forEach((controlName) => {
            const control = this.form.get(controlName);
            if (control) {
                if (this.editableFields.includes(controlName)) {
                    control.enable();
                } else {
                    control.disable();
                }
            }
        });
    }

    onSave(): void {
        if (!this.form.valid) {
            this.markFormGroupTouched();
            this.snackbar.open('Please fix validation errors', 'Close', { duration: 5000 });
            return;
        }

        const shopFormData = this.shopFormService.convertFormToShopForm(this.form);

        this.shopFormService.updateShopForm(shopFormData).subscribe({
            next: (result) => {
                this.snackbar.open('Shop form saved successfully', 'Close', { duration: 3000 });
                this.dialogRef.close(result);
            },
            error: () => {
                this.snackbar.open('Shop form failed to save', 'Error', {
                    duration: 3000,
                    panelClass: 'error-snack',
                });
            },
        });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    private markFormGroupTouched(): void {
        if (!this.form) return;

        Object.keys(this.form.controls).forEach((key) => {
            const control = this.form.get(key);
            control?.markAsTouched();
        });
    }
}
